import {HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {CurrentUserAdapter} from '@drl/core/auth/adapters/current-user-adapter.service';
import {LoginAdapter} from '@drl/core/auth/adapters/login-adapter.service';
import {User} from '@drl/core/auth/models/user.model';
import {environment} from '@drl/env/environment';
import {NgxPermissionsService} from 'ngx-permissions';
import {forkJoin, Observable, of, throwError, Subject} from 'rxjs';
import {catchError, concatMap, switchMap, tap} from 'rxjs/operators';

import {AnyAdapter} from '../api/adapters/any-adapter.service';
import {ApiService} from '../api/api.service';
import {AuthTokenSkipHeader} from '../interceptors/auth.interceptor';
import {ErrToastSkipHeader} from '../interceptors/error.interceptor';
import {UserSessionStoreService as StoreService} from '../store/user-session-store.service';
import * as FullStory from '@fullstory/browser';
import * as moment from 'moment';
import {
  ForgetPasswordCommand,
  GetCurrentUserCommand,
  GetTokenCommand,
  LoginCommand,
  RefreshTokenCommand,
  ResetPasswordCommand,
  ValidateSendRecordsTokenCommand,
  VerifyResetPasswordLinkCommand,
  OtpVerificationCommand,
  ResendOtpCommand,
} from './commands';
import {LoginModel} from './models/login.model';
import {LogoutCommand} from './commands/logout.command';
import {PasswordExpiryEmitter} from '../../password-expiry-popup/services/password-expiry-emitter.service';
import {PasswordExpiry} from '../../password-expiry-popup/services/password-expiry.service';
import {PasswordExpiryEnum} from '../../password-expiry-popup/password-expiry-key.enum';
import {ResetPasswordExpiryCommand} from './commands/reset-password-expiry.command';
import * as CryptoJS from 'crypto-js';
import {EncryptPayloadModel} from './models/encrpt-payload.model';
import {EncrptPayloadAdapter} from './adapters/encrpt-payload-adapter.service';
import {EncryptionService} from './encryption.service';
import {LoginTokenCommand} from './commands/login-token.command';
@Injectable()
export class AuthService {
  redirectUrl: string;
  private readonly authHeaders = new HttpHeaders().set(AuthTokenSkipHeader, '');

  constructor(
    private readonly router: Router,
    private readonly store: StoreService,
    private readonly apiService: ApiService,
    private readonly currentUserAdapter: CurrentUserAdapter,
    private readonly encryptAdapter: EncrptPayloadAdapter,
    private readonly anyAdapter: AnyAdapter,
    private readonly permissionsService: NgxPermissionsService,
    private readonly passwordExpiryEmitter: PasswordExpiryEmitter,
    private readonly passwordExpiry: PasswordExpiry,
    private encryptService: EncryptionService,
  ) {}

  public isLoggedIn(): Observable<boolean> {
    return this.currentUser().pipe(
      switchMap(user => {
        if (user && user.id) {
          return of(true);
        } else {
          return of(false);
        }
      }),
    );
  }

  public currentUser(): Observable<User> {
    const user = this.store.getUser();
    const hasToken = !!this.store.getAccessToken();
    if (user && user.id) {
      this.permissionsService.loadPermissions(user.permissions);
      return of(user);
    } else if (!hasToken) {
      return throwError('No token available');
    } else {
      const command: GetCurrentUserCommand<User> = new GetCurrentUserCommand(
        this.apiService,
        this.currentUserAdapter,
      );
      command.parameters = {
        headers: new HttpHeaders().set(ErrToastSkipHeader, ''),
      };
      command.resEncrypted = true;
      return command.execute().pipe(
        tap(res => {
          this.permissionsService.loadPermissions(res.permissions);
          this.store.setUser(res);
          if (environment.orgId) {
            this.subscribeToFS();
          }
        }),
      );
    }
  }

  // sonarignore:start
  public forgetPasswordReq(email: string): Observable<any> {
    const command: ForgetPasswordCommand<any> = new ForgetPasswordCommand(
      this.apiService,
      this.anyAdapter,
    );

    const forgotModel = {
      username: email.toLowerCase(),
      tenantName: this.store.getTenantName(),
      client_id: environment.clientId,
      client_secret: environment.clientSecret,
    };
    const encrpted = this.encryptService.encryptData(
      JSON.stringify(forgotModel),
    );

    // sonarignore:end
    command.parameters = {
      data: {
        data: encrpted,
      },
      observe: 'response',
      headers: this.authHeaders,
    };
    return command.execute();
  }

  public resetPasswordExpiryReq(email: string): Observable<{success: true}> {
    // // sonarignore:start
    const command: ResetPasswordExpiryCommand<any> =
      new ResetPasswordExpiryCommand(this.apiService, this.anyAdapter);
    // sonarignore:end
    command.parameters = {
      data: {
        username: email.toLowerCase(),
        client_id: environment.clientId,
        client_secret: environment.clientSecret,
        tenantName: this.store.getTenantName(),
      },
      observe: 'response',
      headers: this.authHeaders,
    };
    return command.execute();
  }

  // sonarignore:start
  public verifyResetPasswordLink(
    token: string,
    tenantName: string,
  ): Observable<any> {
    const command: VerifyResetPasswordLinkCommand<any> =
      new VerifyResetPasswordLinkCommand(this.apiService, this.anyAdapter);
    // sonarignore:end
    command.parameters = {
      query: new HttpParams().set('token', token).set('tenantName', tenantName),
      headers: this.authHeaders,
    };
    return command.execute();
  }

  // sonarignore:start
  public resetPassword(
    token: string,
    password: string,
    tenantName: string,
  ): Observable<any> {
    const command: ResetPasswordCommand<any> = new ResetPasswordCommand(
      this.apiService,
      this.anyAdapter,
    );
    // sonarignore:end
    command.parameters = {
      data: {
        token,
        password,
        client_id: environment.clientId,
        client_secret: environment.clientSecret,
      },
      observe: 'response',
      headers: this.authHeaders,
      query: new HttpParams().set('tenantName', tenantName),
    };
    return command.execute();
  }

  // sonarignore:start
  public login(
    username: string,
    password: string,
    tenantName: string,
    newLogin?: boolean,
  ): Observable<any> {
    // sonarignore:end
    this.store.setUser({
      username,
    } as User);
    this.store.setTenantName(tenantName);
    const command: LoginTokenCommand<EncryptPayloadModel> =
      new LoginTokenCommand(this.apiService, this.encryptAdapter);
    const loginModel: LoginModel = {
      username: username.toLowerCase(),
      password,
      tenantName,
      clientId: environment.clientId,
      clientSecret: environment.clientSecret,
    };
    if (newLogin) {
      loginModel.newLogin = newLogin;
    }
    const encrpted = this.encryptService.encryptData(
      JSON.stringify(loginModel),
    );
    command.parameters = {
      data: {
        data: encrpted,
      },
      observe: 'response',
      headers: this.authHeaders,
    };
    return command.execute();
  }

  // sonarignore:start
  public loginPatient(
    token: string,
    externalId: string,
    password: string,
  ): Observable<any> {
    const validateCommand: ValidateSendRecordsTokenCommand<{
      phone: string;
    }> = new ValidateSendRecordsTokenCommand(this.apiService, this.anyAdapter);
    validateCommand.parameters = {
      query: new HttpParams().set('token', token).set('externalid', externalId),
      headers: this.authHeaders,
    };
    return validateCommand
      .execute()
      .pipe(
        concatMap(resp => {
          if (resp.phone) {
            this.store.setUser({
              username: resp.phone,
            } as User);
            const command: LoginCommand<any> = new LoginCommand(
              this.apiService,
              this.encryptAdapter,
            );
            command.parameters = {
              data: {
                username: resp.phone,
                password,
                clientId: environment.clientId,
                clientSecret: environment.clientSecret,
              },
              observe: 'response',
              headers: this.authHeaders,
            };
            return forkJoin(command.execute(), of(resp.phone));
          }
        }),
      )
      .pipe(
        concatMap(loginResp => {
          const command: GetTokenCommand<EncryptPayloadModel> =
            new GetTokenCommand(this.apiService, this.encryptAdapter);
          const tokenModel = {
            username: loginResp[1],
            clientId: environment.clientId,
            code: loginResp[0].body.code,
          };
          const encrpted = this.encryptService.encryptData(
            JSON.stringify(tokenModel),
          );

          command.parameters = {
            data: {
              data: encrpted,
            },
            headers: this.authHeaders,
          };
          return command.execute().pipe(
            tap((response: any) => {
              if (response.accessToken && response.refreshToken) {
                this.store.saveAccessToken(response.accessToken);
                this.store.saveRefreshToken(response.refreshToken);
                this.checkPrePasswordExpiry();
              }
            }),
          );
        }),
      );
  }
  // sonarignore:end

  public authorize(response?: {
    accessToken?: string;
    refreshToken?: string;
    pubnubToken?: string;
  }): void {
    if (response.accessToken && response.refreshToken && response.pubnubToken) {
      this.store.saveAccessToken(response.accessToken);
      this.store.saveRefreshToken(response.refreshToken);
      this.store.savePubnubToken(response.pubnubToken);
      this.router.navigate([environment.defaultPath]);
      this.checkPrePasswordExpiry();
    }
  }

  public otpPageRedirect(): void {
    this.router.navigate(['/verify-otp']);
  }

  public resendOtp(): Observable<any> {
    const command: ResendOtpCommand<EncryptPayloadModel> = new ResendOtpCommand(
      this.apiService,
      this.encryptAdapter,
    );
    let optPayload = {
      username: this.store.getUser()?.username,
      tenantName: this.store.getTenantName(),
      clientId: environment.clientId,
      clientSecret: environment.clientSecret,
    };
    const encrpted = this.encryptService.encryptData(
      JSON.stringify(optPayload),
    );
    command.parameters = {
      data: {
        data: encrpted,
      },
      observe: 'response',
      headers: this.authHeaders,
    };
    return command.execute();
  }

  public otpVerification(otp: string): Observable<any> {
    const command: OtpVerificationCommand<EncryptPayloadModel> =
      new OtpVerificationCommand(this.apiService, this.encryptAdapter);
    let optPayload = {
      username: this.store.getUser()?.username,
      tenantName: this.store.getTenantName(),
      clientId: environment.clientId,
      clientSecret: environment.clientSecret,
      password: otp,
    };
    const encrpted = this.encryptService.encryptData(
      JSON.stringify(optPayload),
    );
    command.parameters = {
      data: {
        data: encrpted,
      },
      observe: 'response',
      headers: this.authHeaders,
    };
    return command.execute();
  }

  // sonarignore:start
  public refreshToken(): Observable<any> {
    // sonarignore:end
    const refreshToken = this.store.getRefreshToken();
    const tenantName = this.store.getTenantName();
    if (!refreshToken) {
      return of(false);
    }
    // sonarignore:start
    const command: RefreshTokenCommand<any> = new RefreshTokenCommand(
      this.apiService,
      this.anyAdapter,
    );

    // sonarignore:end
    command.parameters = {
      data: {
        refreshToken,
        tenantName,
      },
    };
    return command
      .execute()
      .pipe(
        tap(
          response => {
            if (
              response.accessToken &&
              response.refreshToken &&
              response.pubnubToken
            ) {
              this.store.clearAll();
              this.store.saveAccessToken(response.accessToken);
              this.store.saveRefreshToken(response.refreshToken);
              this.store.savePubnubToken(response.pubnubToken);

              setTimeout(() => {
                this.checkPrePasswordExpiry();
              }, 0);
            } else {
              this.logout();
            }
          },
          err => {
            this.logout();
          },
        ),
      )
      .pipe(catchError(this.handleError));
  }

  public logout(): void {
    // send logout request first
    // sonarignore:start
    const command: LogoutCommand<any> = new LogoutCommand(
      this.apiService,
      this.anyAdapter,
    );
    // sonarignore:end
    command.parameters = {
      data: {},
      headers: new HttpHeaders().set(
        'refresh-token',
        this.store.getRefreshToken(),
      ),
    };
    command.execute().subscribe(
      response => {
        this.store.clearAll();
        this.permissionsService.flushPermissions();
        window.location.href = 'login';
      },
      err => {
        this.store.clearAll();
        this.permissionsService.flushPermissions();
        window.location.href = 'login';
      },
    );
  }

  private handleError(error: HttpErrorResponse) {
    return throwError('Something bad happened; please try again later.');
  }

  private subscribeToFS() {
    const userId = this.store.getUser().id;
    const userName = this.store.getUser().username;
    FullStory.identify(userId.toString(), {displayName: userName});
  }
  public checkPrePasswordExpiry() {
    this.currentUser().subscribe(
      user => {
        const alertEmailDate = this.store.getPasswordExpiryAlert();
        const isSameEmailDate =
          alertEmailDate &&
          alertEmailDate.userEmail === user.email &&
          new Date(alertEmailDate.date).toDateString() ===
            new Date().toDateString();
        if (isSameEmailDate) {
          return;
        } else {
          this.store.savePasswordExpiryAlert(user.email);
          if (moment(user.passwordExpiryTime).format() > moment().format()) {
            const remainingDays = moment(user.passwordExpiryTime)
              .startOf('day')
              .diff(moment().startOf('day'), 'd');
            if (
              remainingDays <= Number(environment.maxCountExpiryDays) &&
              !!Number(environment.isPasswordExpiryRequired)
            ) {
              this.passwordExpiry.expiryType =
                PasswordExpiryEnum.PrePasswordExpired;
              this.passwordExpiry.days = remainingDays;
              this.passwordExpiryEmitter.passwordExpiryTypeEmitter.next(
                this.passwordExpiry,
              );
            }
          }
        }
      },
      err => {
        // do nithing
      },
    );
  }
  sendExpiryResetEmail(): Observable<boolean> {
    const sendResponse = new Subject<boolean>();
    this.currentUser().subscribe(
      user => {
        const userEmail = user.email;
        this.resetPasswordExpiryReq(userEmail).subscribe(
          res => {
            sendResponse.next(true);
          },
          err => {
            sendResponse.error(err);
          },
        );
      },
      err => {
        sendResponse.error(err);
      },
    );
    return sendResponse.asObservable();
  }
}
